// The audition → callback → offer → accepted journey (the heart of RepTree).
export const OPPORTUNITY_STAGES = [
    'audition',
    'self_tape', // a self-tape was uploaded for this audition (auto-set on client upload)
    'callback',
    'recall', // called back again (2nd+ callback)
    'pencil', // penciled — soft hold from production
    'hold', // on hold — first refusal / availability locked
    'pinned', // pinned — production is holding the actor for specific shoot dates
    'offer',
    'accepted',
    'booked',
    'passed', // actor passed on the audition
    'declined', // production passed / lost
];
/** Stages that require a pinned date range (the dates production is holding). */
export const DATE_RANGE_STAGES = ['pinned'];
/** The quick forward path an agent one-taps through. Extended stages are set explicitly. */
export const STAGE_FLOW = ['audition', 'self_tape', 'callback', 'offer', 'accepted', 'booked'];
/**
 * Optional power stages between callback and offer (recall / pencil / hold / pinned).
 * The board only shows their columns when in use; the stage menu can always set them.
 */
export const EXTENDED_STAGES = ['recall', 'pencil', 'hold', 'pinned'];
/** Every stage in canonical display order (board columns, stage menus).
 *  'declined' is retired from the UI — it and 'passed' merged into one
 *  "Not moving forward" outcome (legacy declined rows render in that lane). */
export const ORDERED_STAGES = [
    'audition', 'self_tape', 'callback', 'recall', 'pencil', 'hold', 'pinned', 'offer', 'accepted', 'booked', 'passed',
];
/** Legacy stage values folded into another lane for display. */
export const STAGE_ALIASES = { declined: 'passed' };
export const canonicalStage = (s) => STAGE_ALIASES[s] ?? s;
/** Terminal/negative outcomes. */
export const TERMINAL_STAGES = ['booked', 'passed', 'declined'];
/**
 * The patch a stage choice implies — the ONE place that rule lives, shared by the board card's stage
 * menu, the audition table's stage cell and bulk moves.
 *
 * Returns `'pin'` rather than a patch when the target is `pinned`: pinning can't be expressed as a
 * patch because it needs the dates production is holding first, so the caller must collect them.
 */
export function stagePatch(s) {
    // A `c_`-prefixed custom lane rides on top of the built-in stage rather than replacing it.
    if (s.startsWith('c_'))
        return { custom_stage: s };
    if (s === 'pinned')
        return 'pin';
    // 'passed' carries the terminal flag; the pre-send lanes un-mark submitted. A built-in stage clears any custom lane.
    return s === 'passed'
        ? { stage: s, passed: true, custom_stage: null }
        : { stage: s, custom_stage: null, ...(s === 'audition' || s === 'self_tape' ? { submitted: false } : {}) };
}
export const STAGE_META = {
    audition: { label: 'Audition', tone: 'sky', order: 0 },
    self_tape: { label: 'Self Tape Attached', tone: 'sky', order: 1, hint: 'Tape uploaded — review & send' },
    callback: { label: 'Callback', tone: 'violet', order: 2 },
    recall: { label: 'Recall', tone: 'violet', order: 3, hint: 'Called back again' },
    pencil: { label: 'Pencil', tone: 'amber', order: 4, hint: 'Soft hold from production' },
    hold: { label: 'Hold', tone: 'amber', order: 5, hint: 'On hold — availability locked' },
    pinned: { label: 'Pinned', tone: 'amber', order: 6, hint: 'Held for shoot dates' },
    offer: { label: 'Offer', tone: 'amber', order: 7 },
    accepted: { label: 'Accepted', tone: 'emerald', order: 8 },
    booked: { label: 'Booked', tone: 'green', order: 9 },
    passed: { label: 'Not moving forward', tone: 'slate', order: 10, hint: 'Passed or not selected' },
    declined: { label: 'Not moving forward', tone: 'slate', order: 11, hint: 'Legacy — same as Not moving forward' },
};
/** A booked opportunity is still an audition row server-side, but presents to users as a "Job". */
export const isJobStage = (stage) => canonicalStage(stage) === 'booked';
/** The noun an opportunity reads as in the UI — "Job" once booked, otherwise "Audition". */
export const oppNoun = (stage) => (isJobStage(stage) ? 'Job' : 'Audition');
export function nextStage(stage) {
    // Quick advance: extended stages fold forward into the main path (recall/pencil/hold → offer).
    if (EXTENDED_STAGES.includes(stage))
        return 'offer';
    const i = STAGE_FLOW.indexOf(stage);
    if (i === -1 || i >= STAGE_FLOW.length - 1)
        return null;
    return STAGE_FLOW[i + 1] ?? null;
}
// ── Self-tape review (agent reviews client uploads) ─────────────────────────
export const REVIEW_STATUSES = ['pending', 'approved', 'retake_requested'];
export const REVIEW_META = {
    pending: { label: 'Awaiting review' },
    approved: { label: 'Approved' },
    retake_requested: { label: 'Retake requested' },
};
export const OPPORTUNITY_TYPES = ['self_tape', 'in_person', 'callback', 'virtual', 'other'];
export const OPPORTUNITY_TYPE_LABELS = {
    self_tape: 'Self-tape',
    in_person: 'In person',
    callback: 'Callback',
    virtual: 'Virtual',
    other: 'Other',
};
// Role size / billing category — the color-coded legend from a real agent's audition log.
export const ROLE_SIZES = [
    'lead',
    'supporting',
    'series_regular',
    'pilot_sr',
    'guest_star',
    'co_star',
    'recurring',
    'day_player',
    'other',
];
export const ROLE_SIZE_LABELS = {
    lead: 'Lead',
    supporting: 'Supporting',
    series_regular: 'Series Regular',
    pilot_sr: 'Pilot — Series Regular',
    guest_star: 'Guest Star',
    co_star: 'Co-Star',
    recurring: 'Recurring',
    day_player: 'Day Player',
    other: 'Other',
};
// Medium / project category (the "Additional Notes" colour column).
export const MEDIUMS = [
    'film_tv',
    'commercial',
    'theater',
    'voiceover',
    'print',
    'new_media',
    'promo',
    'variety_show',
    'workshop',
];
export const MEDIUM_LABELS = {
    film_tv: 'Film / TV',
    commercial: 'Commercial',
    theater: 'Theater',
    voiceover: 'Voiceover',
    print: 'Print',
    new_media: 'New Media',
    promo: 'Promo',
    variety_show: 'Variety Show',
    workshop: 'Workshop',
};
/** Reasons a tape was not submitted (parsed from the real sheet's "No - …" notes). */
export const NOT_SUBMITTED_REASONS = [
    'passing', // actor passed on the audition (passed = true)
    'didnt_turn_in',
    'late',
    'done_acting',
    'wanted_other_role',
    'in_person',
    'other',
];
//# sourceMappingURL=opportunity.js.map